// Recording controls + live status for a meeting in `recording`
// state (docs/product-specs/live-meeting.md).

import { html, LitElement } from "lit";
import { Recorder } from "../capture/recorder";
import { ApiError, meetings } from "../lib/api";

export class MmRecorder extends LitElement {
  static override properties = {
    meetingId: { attribute: false },
  };

  meetingId = 0;

  private recorder: Recorder | null = null;
  private active = false;
  private stopping = false;
  private level = 0;
  private queueDepth = 0;
  private sharing = false;
  private notice = "";
  private error = "";

  override createRenderRoot(): HTMLElement {
    return this;
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    // Navigating away abandons capture (meeting stays `recording`;
    // the detail view offers Stop & summarize).
    void this.recorder?.stop();
  }

  private async start(): Promise<void> {
    this.error = "";
    this.recorder = new Recorder(this.meetingId, {
      onLevel: (rms) => {
        this.level = Math.min(1, rms * 6);
        this.requestUpdate();
      },
      onQueueDepth: (depth) => {
        this.queueDepth = depth;
        this.requestUpdate();
      },
      onSystemEnded: () => {
        this.sharing = false;
        this.notice = "Tab audio sharing ended — continuing with mic only.";
        this.requestUpdate();
      },
      onUploadError: (message) => {
        this.error = message;
        this.requestUpdate();
      },
    });
    try {
      // Device choice comes from the saved prefs (mm-device-picker /
      // Settings → Devices); the mixer reads them.
      await this.recorder.start();
      this.active = true;
    } catch (e) {
      this.error =
        e instanceof Error ? e.message : "microphone permission denied";
      this.recorder = null;
    }
    this.requestUpdate();
  }

  private async shareTab(): Promise<void> {
    if (!this.recorder) return;
    this.notice = "";
    try {
      const ok = await this.recorder.addSystemAudio();
      this.sharing = ok;
      this.notice = ok
        ? ""
        : "No audio track shared — tick “share tab audio” in the picker.";
    } catch {
      this.notice = "Tab sharing cancelled.";
    }
    this.requestUpdate();
  }

  private async stop(): Promise<void> {
    if (!this.recorder) return;
    this.stopping = true;
    this.requestUpdate();
    try {
      await this.recorder.stop(); // flush + drain uploads first
      await meetings.stop(this.meetingId);
      this.dispatchEvent(new CustomEvent("recording-stopped", { bubbles: true }));
    } catch (e) {
      this.error = e instanceof ApiError ? e.message : "stop failed";
    } finally {
      this.active = false;
      this.stopping = false;
      this.recorder = null;
      this.requestUpdate();
    }
  }

  override render() {
    const pct = Math.round(this.level * 100);
    return html`
      <div class="toolbar" style="margin-bottom: 0.9rem">
        ${!this.active
          ? html`
              <mm-device-picker></mm-device-picker>
              <button class="primary-button" @click=${this.start}>
                ● Start recording
              </button>
            `
          : html`
              <span class="row"><span class="rec-dot"></span> Recording</span>
              <button
                class="ghost-button"
                ?disabled=${this.sharing}
                @click=${this.shareTab}
              >
                ${this.sharing ? "Tab audio live" : "Share tab audio"}
              </button>
              <button
                class="danger-button"
                ?disabled=${this.stopping}
                @click=${this.stop}
              >
                ${this.stopping ? "Finishing uploads…" : "■ Stop & summarize"}
              </button>
            `}
      </div>
      ${this.active
        ? html`
            <div class="level-meter" style="margin-bottom: 0.6rem">
              <div
                class="level-meter-fill ${pct > 85 ? "hot" : ""}"
                style="width: ${pct}%"
              ></div>
            </div>
            ${this.queueDepth > 5
              ? html`<div class="message-card message-card-warn">
                  <p>${this.queueDepth} chunks waiting to upload…</p>
                </div>`
              : ""}
          `
        : ""}
      ${this.notice
        ? html`<div class="message-card message-card-info"><p>${this.notice}</p></div>`
        : ""}
      ${this.error
        ? html`<div class="message-card message-card-error"><p>${this.error}</p></div>`
        : ""}
    `;
  }
}

customElements.define("mm-recorder", MmRecorder);
